import React, { useState, useEffect } from 'react';
import apiService from '../services/api.service';


const Payments = () => {
  // States for payment management
  const [payments, setPayments] = useState([]);
  const [orders, setOrders] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [currentPayment, setCurrentPayment] = useState({
    id: null,
    orderId: '',
    amount: 0,
    paymentDate: new Date().toISOString().substr(0, 10), // Today's date in YYYY-MM-DD format
    paymentMethod: 'nakit', // nakit, havale, kredi_kartı, diğer
    notes: '',
    receiptFile: null
  });
  
  // State for file uploads
  const [selectedFile, setSelectedFile] = useState(null);
  
  // UI states
  const [modalVisible, setModalVisible] = useState(false);
  const [modalMode, setModalMode] = useState('add'); // 'add', 'edit', or 'view'
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [receiptModalVisible, setReceiptModalVisible] = useState(false);
  const [currentReceiptUrl, setCurrentReceiptUrl] = useState('');
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [filterSupplier, setFilterSupplier] = useState('');
  const [filterOrder, setFilterOrder] = useState('');
  const [filterPaymentMethod, setFilterPaymentMethod] = useState('all');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  
  // Payment method options
  const paymentMethodOptions = [
    { value: 'nakit', label: 'Nakit' },
    { value: 'havale', label: 'Havale/EFT' },
    { value: 'kredi_kartı', label: 'Kredi Kartı' },
    { value: 'diğer', label: 'Diğer' }
  ];
  
  // Fetch payments with filters
  const fetchPayments = async () => {
    setLoading(true);
    try {
      // Prepare filter parameters
      const filters = {};
      if (searchTerm) filters.search = searchTerm;
      if (filterOrder) filters.orderId = filterOrder;
      if (filterPaymentMethod !== 'all') filters.paymentMethod = filterPaymentMethod;
      
      // Use the correct parameter names expected by the backend
      if (filterDateFrom) filters.startDate = filterDateFrom;
      if (filterDateTo) filters.endDate = filterDateTo;
      
      console.log('Loading payments...', filters);
      const response = await apiService.getPayments(filters);
      console.log('Payments response:', response);
      setPayments(response.data);
      setError(null);
    } catch (err) {
      console.error('Error loading payments:', err);
      setError('Failed to load payments: ' + 
        (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };
  
  // Fetch orders for dropdown
  const fetchOrders = async () => {
    try {
      const response = await apiService.getOrders({});
      setOrders(response.data);
      
      // Extract unique supplier IDs from orders to fetch supplier details
      const supplierIds = [...new Set(response.data.map(order => order.supplierId))];
      
      // Fetch supplier details for these IDs
      if (supplierIds.length > 0) {
        await fetchSuppliersByIds(supplierIds);
      }
    } catch (err) {
      console.error('Error loading orders:', err);
      // No need to set error as this is secondary data
    }
  };
  
  // Fetch suppliers by IDs
  const fetchSuppliersByIds = async (supplierIds) => {
    try {
      // Ideally, you would have an API to fetch suppliers by IDs
      // For now, fetching all suppliers and filtering in frontend
      const response = await apiService.getSuppliers({ isActive: true });
      const filteredSuppliers = response.data.filter(supplier => 
        supplierIds.includes(supplier.id)
      );
      setSuppliers(filteredSuppliers);
    } catch (err) {
      console.error('Error loading suppliers:', err);
    }
  };
  
  // Load data on component mount
  useEffect(() => {
    fetchPayments();
    fetchOrders();
  }, []);
  
  // Reload payments when filters change
  useEffect(() => {
    fetchPayments();
  }, [searchTerm, filterOrder, filterPaymentMethod, filterDateFrom, filterDateTo, filterSupplier]);
  
  // Handle payment form input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    setCurrentPayment({
      ...currentPayment,
      [name]: type === 'checkbox' ? checked : value
    });
  };
  
  // Handle file selection
  const handleFileChange = (e) => {
    setSelectedFile(e.target.files[0]);
  };
  
  // Upload receipt document
  const uploadReceipt = async (paymentId) => {
    if (!selectedFile) {
      alert('Lütfen bir dosya seçin');
      return;
    }
    
    const formData = new FormData();
    formData.append('receipt', selectedFile);
    
    try {
      const response = await apiService.uploadPaymentReceipt(paymentId, formData);
      console.log('Upload response:', response);
      
      // Refresh payments to get updated document URLs
      fetchPayments();
      
      // Reset file selection
      setSelectedFile(null);
      setUploadProgress(0);
      
      return response.data.fileUrl;
    } catch (err) {
      console.error('Error uploading receipt:', err);
      alert('Failed to upload receipt: ' + 
        (err.response?.data?.error || err.message));
      setUploadProgress(0);
      return null;
    }
  };
  
  // Show receipt in modal
  const showReceipt = (url) => {
    setCurrentReceiptUrl(url);
    setReceiptModalVisible(true);
  };
  
  // Submit payment form
  const handleSubmit = async (e) => {
    e.preventDefault();
  
    // Validate form
    if (!currentPayment.orderId) {
      setError('Lütfen bir sipariş seçin');
      return;
    }
    
    if (!currentPayment.amount || currentPayment.amount <= 0) {
      setError('Lütfen geçerli bir ödeme tutarı girin');
      return;
    }
  
    try {
      // Format data for API
      let paymentData = {
        ...currentPayment,
        amount: parseFloat(currentPayment.amount)
      };
      
      // Ensure date is in correct format
      if (!paymentData.paymentDate) {
        paymentData.paymentDate = new Date().toISOString().substr(0, 10);
      }
      
      console.log('Sending payment data:', paymentData);
      
      let paymentId;
      
      if (modalMode === 'add') {
        // Create new payment
        const response = await apiService.createPayment(paymentData);
        paymentId = response.data.id;
      } else {
        // Update existing payment
        const response = await apiService.updatePayment(currentPayment.id, paymentData);
        paymentId = currentPayment.id;
      }
  
      // Upload receipt if a file is selected
      if (selectedFile && paymentId) {
        await uploadReceipt(paymentId);
      }
  
      // Success actions
      setModalVisible(false);
      resetForm();
      fetchPayments();
    } catch (err) {
      console.error('Error saving payment:', err);
      setError('Failed to save payment: ' + 
        (err.response?.data?.error || err.message));
    }
  };
  
  // Delete payment
  const handleDelete = async (id) => {
    if (!window.confirm('Bu ödemeyi silmek istediğinizden emin misiniz?')) {
      return;
    }

    try {
      await apiService.deletePayment(id);
      fetchPayments();
    } catch (err) {
      console.error('Error deleting payment:', err);
      setError('Failed to delete payment: ' + 
        (err.response?.data?.error || err.message));
    }
  };
  
  // View payment details
  const handleView = (payment) => {
    setCurrentPayment({
      ...payment,
      orderId: payment.orderId || ''
    });
    setModalMode('view');
    setModalVisible(true);
  };
  
  // Edit payment
  const handleEdit = (payment) => {
    setCurrentPayment({
      id: payment.id,
      orderId: payment.orderId || '',
      amount: payment.amount || 0,
      paymentDate: payment.paymentDate || new Date().toISOString().substr(0, 10),
      paymentMethod: payment.paymentMethod || 'nakit',
      notes: payment.notes || '',
      receiptFile: payment.receiptFile || null
    });
    
    setModalMode('edit');
    setModalVisible(true);
  };
  
  // Open add payment modal
  const handleAdd = () => {
    resetForm();
    setModalMode('add');
    setModalVisible(true);
  };
  
  // Reset payment form
  const resetForm = () => {
    setCurrentPayment({
      id: null,
      orderId: '',
      amount: 0,
      paymentDate: new Date().toISOString().substr(0, 10),
      paymentMethod: 'nakit',
      notes: '',
      receiptFile: null
    });
    
    setSelectedFile(null);
    setUploadProgress(0);
  };
  
  // Format currency
  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined || amount === '') return '-';
    return new Intl.NumberFormat('tr-TR', { 
      style: 'currency', 
      currency: 'TRY',
      minimumFractionDigits: 2
    }).format(amount);
  };
  
  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('tr-TR');
  };
  
  // Get order details by ID
  const getOrderDetails = (orderId) => {
    const order = orders.find(o => o.id === orderId);
    if (!order) return { number: '-', supplier: '-' };
    
    const supplier = suppliers.find(s => s.id === order.supplierId);
    return {
      number: `#${order.id}`,
      supplier: supplier ? supplier.name : '-',
      date: order.orderDate ? formatDate(order.orderDate) : '-',
      total: order.totalAmount ? formatCurrency(order.totalAmount) : '-'
    };
  };
  
  // Get payment method label
  const getPaymentMethodLabel = (method) => {
    const option = paymentMethodOptions.find(opt => opt.value === method);
    return option ? option.label : method;
  };

  return (
    <div className="tab-content">
      <div className="content-header">
        <h3>Ödemeler</h3>
        <button 
          className="add-button"
          onClick={handleAdd}
        >
          <span className="icon">+</span>
          <span className="button-text">Yeni Ödeme Ekle</span>
        </button>
      </div>

      <div className="filters">
        <div className="search-box">
          <input 
            type="text" 
            placeholder="Ödemelerde ara..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="filter-box">
          <label htmlFor="filterSupplier">Tedarikçi:</label>
          <select 
            id="filterSupplier" 
            value={filterSupplier}
            onChange={(e) => setFilterSupplier(e.target.value)}
          >
            <option value="">Tümü</option>
            {suppliers.map(supplier => (
              <option key={supplier.id} value={supplier.id}>
                {supplier.name}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-box">
          <label htmlFor="filterOrder">Sipariş:</label>
          <select 
            id="filterOrder" 
            value={filterOrder}
            onChange={(e) => setFilterOrder(e.target.value)}
          >
            <option value="">Tümü</option>
            {orders.map(order => (
              <option key={order.id} value={order.id}>
                Sipariş #{order.id} ({getOrderDetails(order.id).supplier})
              </option>
            ))}
          </select>
        </div>

        <div className="filter-box">
          <label htmlFor="filterPaymentMethod">Ödeme Tipi:</label>
          <select 
            id="filterPaymentMethod" 
            value={filterPaymentMethod}
            onChange={(e) => setFilterPaymentMethod(e.target.value)}
          >
            <option value="all">Tümü</option>
            {paymentMethodOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-box">
          <label htmlFor="filterDateFrom">Başlangıç:</label>
          <input 
            type="date" 
            id="filterDateFrom" 
            value={filterDateFrom}
            onChange={(e) => setFilterDateFrom(e.target.value)}
          />
        </div>

        <div className="filter-box">
          <label htmlFor="filterDateTo">Bitiş:</label>
          <input 
            type="date" 
            id="filterDateTo" 
            value={filterDateTo}
            onChange={(e) => setFilterDateTo(e.target.value)}
          />
        </div>
      </div>

      {loading ? (
        <div className="loading">Yükleniyor...</div>
      ) : error ? (
        <div className="error-message">{error}</div>
      ) : (
        <div className="payments-list">
          <table className="data-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Sipariş</th>
                <th>Tedarikçi</th>
                <th>Tarih</th>
                <th>Tutar</th>
                <th>Ödeme Tipi</th>
                <th>Dekont</th>
                <th>İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {payments.length === 0 ? (
                <tr>
                  <td colSpan="8" className="no-data">Ödeme bulunamadı.</td>
                </tr>
              ) : (
                payments.map(payment => {
                  const orderDetails = getOrderDetails(payment.orderId);
                  
                  return (
                    <tr key={payment.id}>
                      <td>{payment.id}</td>
                      <td>{orderDetails.number}</td>
                      <td>{orderDetails.supplier}</td>
                      <td>{formatDate(payment.paymentDate)}</td>
                      <td>{formatCurrency(payment.amount)}</td>
                      <td>{getPaymentMethodLabel(payment.paymentMethod)}</td>
                      <td>
                        {payment.receiptFile ? (
                          <button 
                            className="view-button"
                            onClick={() => showReceipt(payment.receiptFile)}
                            title="Dekontu Görüntüle"
                          >
                            <span className="icon">📄</span>
                          </button>
                        ) : (
                          '-'
                        )}
                      </td>
                      <td>
                        <div className="action-buttons">
                          <button 
                            className="view-button"
                            onClick={() => handleView(payment)}
                            title="Detayları Görüntüle"
                          >
                            <span className="icon">👁️</span>
                          </button>
                          <button 
                            className="edit-button"
                            onClick={() => handleEdit(payment)}
                            title="Düzenle"
                          >
                            <span className="icon">✎</span>
                          </button>
                          <button 
                            className="delete-button"
                            onClick={() => handleDelete(payment.id)}
                            title="Sil"
                          >
                            <span className="icon">✕</span>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Payment Modal */}
      {modalVisible && (
        <div className="modal-overlay">
          <div className="modal standard-modal">
            <div className="modal-header">
              <h3>
                {modalMode === 'add' ? 'Yeni Ödeme Ekle' : 
                 modalMode === 'edit' ? 'Ödeme Düzenle' : 'Ödeme Detayları'}
              </h3>
              <button 
                className="close-button"
                onClick={() => setModalVisible(false)}
              >
                &times;
              </button>
            </div>
            
            <form className="form" onSubmit={handleSubmit}>
              <div className="form-section">
                <h4>Ödeme Bilgileri</h4>
                
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="orderId">Sipariş *</label>
                    <select 
                      id="orderId" 
                      name="orderId" 
                      value={currentPayment.orderId} 
                      onChange={handleInputChange}
                      disabled={modalMode === 'view'}
                      required
                    >
                      <option value="">Sipariş seçin</option>
                      {orders.map(order => {
                        const orderDetails = getOrderDetails(order.id);
                        return (
                          <option key={order.id} value={order.id}>
                            Sipariş #{order.id} - {orderDetails.supplier} ({orderDetails.total})
                          </option>
                        );
                      })}
                    </select>
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="paymentMethod">Ödeme Tipi *</label>
                    <select 
                      id="paymentMethod" 
                      name="paymentMethod" 
                      value={currentPayment.paymentMethod} 
                      onChange={handleInputChange}
                      disabled={modalMode === 'view'}
                      required
                    >
                      {paymentMethodOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="amount">Tutar (₺) *</label>
                    <input 
                      type="number" 
                      id="amount" 
                      name="amount" 
                      value={currentPayment.amount} 
                      onChange={handleInputChange}
                      disabled={modalMode === 'view'}
                      step="0.01"
                      min="0.01"
                      required
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="paymentDate">Ödeme Tarihi *</label>
                    <input 
                      type="date" 
                      id="paymentDate" 
                      name="paymentDate" 
                      value={currentPayment.paymentDate} 
                      onChange={handleInputChange}
                      disabled={modalMode === 'view'}
                      required
                    />
                  </div>
                </div>
                
                <div className="form-group">
                  <label htmlFor="notes">Notlar</label>
                  <textarea 
                    id="notes" 
                    name="notes" 
                    value={currentPayment.notes} 
                    onChange={handleInputChange}
                    rows="3"
                    disabled={modalMode === 'view'}
                  ></textarea>
                </div>
              </div>
              
              {modalMode !== 'view' && (
                <div className="form-section">
                  <h4>Dekont Yükleme</h4>
                  
                  <div className="form-group">
                    <label htmlFor="receipt">Dekont Yükle (PDF, JPG, PNG)</label>
                    <input 
                      type="file" 
                      id="receipt" 
                      onChange={handleFileChange}
                      accept=".pdf,.jpg,.jpeg,.png"
                    />
                  </div>
                  
                  {uploadProgress > 0 && (
                    <div className="upload-progress">
                      <div 
                        className="progress-bar" 
                        style={{ width: `${uploadProgress}%` }}
                      ></div>
                      <span>{uploadProgress}%</span>
                    </div>
                  )}
                  
                  {currentPayment.receiptFile && (
                    <div className="current-file">
                      <p>Mevcut Dekont: {currentPayment.receiptFile.split('/').pop()}</p>
                      <button 
                        type="button" 
                        className="secondary-button"
                        onClick={() => showReceipt(currentPayment.receiptFile)}
                      >
                        Görüntüle
                      </button>
                    </div>
                  )}
                </div>
              )}
              
              <div className="form-actions">
                <button 
                  type="button" 
                  className="cancel-button"
                  onClick={() => setModalVisible(false)}
                >
                  {modalMode === 'view' ? 'Kapat' : 'İptal'}
                </button>
                
                {modalMode !== 'view' && (
                  <button 
                    type="submit" 
                    className="save-button"
                  >
                    Kaydet
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Receipt Viewer Modal */}
      {receiptModalVisible && (
        <div className="modal-overlay">
          <div className="modal receipt-modal">
            <div className="modal-header">
              <h3>Dekont</h3>
              <button 
                className="close-button"
                onClick={() => setReceiptModalVisible(false)}
              >
                &times;
              </button>
            </div>
            
            <div className="receipt-viewer">
              {currentReceiptUrl.endsWith('.pdf') ? (
                <iframe 
                  src={currentReceiptUrl} 
                  width="100%" 
                  height="500px"
                  title="Dekont PDF"
                ></iframe>
              ) : (
                <img 
                  src={currentReceiptUrl} 
                  alt="Dekont" 
                  style={{ maxWidth: '100%', maxHeight: '500px' }}
                />
              )}
            </div>
            <div className="form-actions">
              <a 
                href={currentReceiptUrl} 
                target="_blank" 
                rel="noopener noreferrer" 
                className="secondary-button"
              >
                Yeni Sekmede Aç
              </a>
              <button 
                type="button" 
                className="cancel-button"
                onClick={() => setReceiptModalVisible(false)}
              >
                Kapat
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Payments;